import { AuthSwitchLink } from "@/components/auth/AuthSwitchLink.web";
import { AuthTermsNotice } from "@/components/auth/AuthTermsNotice.web";
import { OrDivider } from "@/components/auth/OrDivider.web";
import { SocialSignInRow } from "@/components/auth/SocialSignInRow.web";
import { ThemedText } from "@/components/ThemedText";
import AlertModal from "@/components/ui/AlertModal.web";
import {
  FormButton,
  FormCard,
  FormPageContainer,
  RHFFormInput,
} from "@/components/ui/forms";
import {
  PhoneAuthFlow,
  type PhoneAuthSuccessPayload,
} from "@/components/ui/PhoneAuthFlow.web";
import { useAuth } from "@/context/AuthContext";
import { auth, firestore } from "@/firebase/web";
import { ensureUserDocExists } from "@/utils/createUserDoc.web";
import { getErrorCode, getErrorMessage } from "@/utils/errors";
import type { PhoneVerificationErrorCode } from "@/utils/phoneVerification.web";
import {
  persistSignupParams,
  readSignupParamsFromQuery,
} from "@/utils/signupParams.web";
import { getUniqueUsername } from "@/utils/username.web";
import { zodResolver } from "@hookform/resolvers/zod";
import { Image } from "expo-image";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { signupSchema, type SignupFormValues } from "./signup.schema";

const SignupScreen = () => {
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;
  const [authLoading, setAuthLoading] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();
  const { user, isLoading: authStateLoading } = useAuth();
  const rawParams = useLocalSearchParams<{
    phoneNumberMethod?: string;
    groupId?: string;
  }>();
  const phoneNumberMethodParam = Array.isArray(rawParams.phoneNumberMethod)
    ? rawParams.phoneNumberMethod[0]
    : rawParams.phoneNumberMethod;
  const groupIdParam = Array.isArray(rawParams.groupId)
    ? rawParams.groupId[0]
    : rawParams.groupId;
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
    onClose?: () => void;
  } | null>(null);

  // Persist segment-specific URL params (phoneNumberMethod, groupId) only for a
  // fresh signup visitor. If an already-signed-up user lands here the auth gate
  // in _layout.web.tsx redirects them away — we must not drop their groupId
  // into storage on the way past, since the params represent signup intent
  // and would otherwise auto-fire a join-request on the next auth → tabs
  // transition.
  useEffect(() => {
    if (authStateLoading || user) return;
    persistSignupParams(
      readSignupParamsFromQuery({
        phoneNumberMethod: phoneNumberMethodParam,
        groupId: groupIdParam,
      }),
    );
  }, [authStateLoading, user, phoneNumberMethodParam, groupIdParam]);

  // Lazy initializer: flips the phone UI on immediately when the URL declares
  // it. `user` is null on first render even for signed-in visitors (auth state
  // hasn't propagated yet) — that's fine because the auth gate will redirect
  // them before the phone UI matters.
  const [phoneFlowActive, setPhoneFlowActive] = useState(
    () => phoneNumberMethodParam === "true",
  );

  const { control, handleSubmit, getValues } = useForm<SignupFormValues>({
    mode: "onTouched",
    resolver: zodResolver(signupSchema),
    defaultValues: { email: "", password: "" },
  });
  const watchedFields = useWatch({ control, name: ["email", "password"] });
  const [watchedEmail, watchedPassword] = watchedFields ?? [];
  const hasRequiredFields = !!watchedEmail && !!watchedPassword;

  const handleSignup = async () => {
    setAuthLoading(true);
    try {
      const { email, password } = getValues();

      // Pre-check: if any sign-in method exists for this email, show the
      // ACCOUNT_EXISTS modal without ever calling
      // createUserWithEmailAndPassword. Firebase's email-enumeration
      // protection (when enabled in the project) makes this return an empty
      // array even for taken emails — the catch below still surfaces
      // `auth/email-already-in-use`, so the modal works either way. This
      // pre-check is the faster, explicit path when enumeration protection
      // is off.
      const existingMethods = await fetchSignInMethodsForEmail(auth, email);
      if (existingMethods.length > 0) {
        setModalInfo({
          title: t("ERROR"),
          message: t("ACCOUNT_EXISTS_MESSAGE"),
        });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      const user = userCredential.user;
      const generatedUsername = await getUniqueUsername(
        firestore,
        user.email?.split("@")[0] || "user",
      );

      await setDoc(doc(firestore, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.email,
        username: generatedUsername,
        createdAt: serverTimestamp(),
        providers: user.providerData.map((p: any) => p.providerId),
        termsAccepted: true,
        recipientMonthlyWAQuota: 1,
        notificationSettings: { globalFeedPosts: true },
      });

      await sendEmailVerification(userCredential.user);

      setModalInfo({
        title: t("VERIFICATION_SENT"),
        message: t("VERIFICATION_EMAIL_SENT"),
        onClose: async () => {
          await signOut(auth);
          router.replace("/(auth)/login");
        },
      });
    } catch (error) {
      const errorCode = getErrorCode(error);
      let translatedErrorMessage;
      if (errorCode === "auth/email-already-in-use") {
        translatedErrorMessage = t("ACCOUNT_EXISTS_MESSAGE");
      }
      setModalInfo({
        title: t("ERROR"),
        message: translatedErrorMessage || getErrorMessage(error),
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const onGoogleButtonPress = async () => {
    setAuthLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // First-time VehiLink user (no Firestore doc): the helper writes the
      // default profile shape with termsAccepted=true (justified by the
      // terms text shown next to the Google button) and we route to
      // /onboarding. Returning user (doc exists): leave their doc alone and
      // let InitialLayout route them — /(tabs)/groups if onboarding is
      // complete, /(auth)/onboarding if not. No more sign-in-then-reject.
      const { created } = await ensureUserDocExists(firestore, user, {
        source: "signup",
      });
      if (created) {
        router.replace("/(auth)/onboarding" as any);
      }
    } catch (error) {
      const errorMessage = getErrorCode(error);
      let translatedErrorMessage;

      if (errorMessage === "auth/popup-closed-by-user") {
        return;
      }
      if (errorMessage === "auth/network-request-failed") {
        translatedErrorMessage = t("NETWORK_ERROR");
      }
      setModalInfo({
        title: t("GOOGLE_SIGN_IN_ERROR"),
        message: translatedErrorMessage || getErrorMessage(error),
      });
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePhoneSignupSendError = ({
    code,
    defaultMessageKey,
  }: {
    code: PhoneVerificationErrorCode;
    defaultMessageKey: string;
  }) => {
    // Signup-specific override: PHONE_ALREADY_IN_USE shows the "already
    // registered" copy instead of the generic send-failed message.
    const messageKey =
      code === "PHONE_ALREADY_IN_USE"
        ? "PHONE_SIGNUP_ALREADY_REGISTERED"
        : defaultMessageKey;
    setModalInfo({ title: t("ERROR"), message: t(messageKey) });
  };

  const handlePhoneSignupConfirm = async ({
    userCredential,
    phonePrefix,
    phoneNumber,
  }: PhoneAuthSuccessPayload) => {
    if (!userCredential) return;
    const user = userCredential.user;
    setPhoneFlowActive(false);

    // First-time VehiLink phone user: helper writes the default doc; then we
    // merge the phone number/prefix on top. Returning phone user (doc
    // already exists): the merge is idempotent (their existing verified
    // record is unchanged) and InitialLayout routes them by onboarding
    // state.
    //
    // The phoneNumber write runs UNCONDITIONALLY (outside `if (created)`) so
    // an interruption between the doc-create and this merge on a subsequent
    // retry still results in verified:true. Leaving it inside `if (created)`
    // was the original bug — a half-written user ended up permanently
    // stuck with `providers:["phone"]` but no `phoneNumber.verified`.
    const { created } = await ensureUserDocExists(firestore, user, {
      source: "signup",
    });

    await setDoc(
      doc(firestore, "users", user.uid),
      {
        phoneNumber: {
          prefix: phonePrefix,
          number: phoneNumber,
          verified: true,
        },
      },
      { merge: true },
    );

    if (created) {
      router.replace("/(auth)/onboarding" as any);
    }
  };

  return (
    <SafeAreaView
      style={[
        styles.safeAreaViewContainer,
        { backgroundColor: isLargeScreen ? "#1e3a8a" : "#ffffff" },
      ]}
      edges={["top", "bottom"]}
    >
      <Head>
        <title>{t("PAGE_TITLE_SIGNUP")}</title>
      </Head>
      <StatusBar style={isLargeScreen ? "light" : "dark"} animated={true} />
      <FormPageContainer>
        <FormCard>
          <View className="items-center mb-6">
            <Image
              source={require("../../assets/images/vehilink-icon.webp")}
              style={{ width: 100, height: 100, borderRadius: 50 }}
              contentFit="cover"
              accessibilityLabel={t("VEHILINK_LOGO_ALT")}
            />
            <ThemedText className="mt-4 text-xl font-normal text-gray-900">
              {t("SIGN_UP")}
            </ThemedText>
          </View>

          {!phoneFlowActive && (
            <>
              <SocialSignInRow
                onGooglePress={onGoogleButtonPress}
                onPhonePress={() => setPhoneFlowActive(true)}
                disabled={authLoading}
              />

              <OrDivider />

              <RHFFormInput
                control={control}
                name="email"
                label={t("EMAIL")}
                placeholder={t("EMAIL_PLACEHOLDER")}
                autoCapitalize="none"
                keyboardType="email-address"
              />

              <RHFFormInput
                control={control}
                name="password"
                label={t("PASSWORD")}
                password
                placeholder={t("PASSWORD_PLACEHOLDER")}
              />

              <FormButton
                onPress={handleSubmit(handleSignup)}
                disabled={!hasRequiredFields || authLoading}
                text={t("SIGN_UP")}
              />

              <AuthTermsNotice introKey="BY_SIGNING_UP_AGREE" />
            </>
          )}

          {phoneFlowActive && (
            <PhoneAuthFlow
              onSuccess={handlePhoneSignupConfirm}
              onCancel={() => setPhoneFlowActive(false)}
              onSendError={handlePhoneSignupSendError}
              subtitleKey="PHONE_SIGNUP_SUBTITLE"
              sendCodeKey="PHONE_SIGNUP_SEND_CODE"
              loading={authLoading}
              onLoadingChange={setAuthLoading}
              footer={<AuthTermsNotice introKey="BY_SIGNING_UP_AGREE" />}
            />
          )}

          <AuthSwitchLink
            promptKey="ALREADY_HAVE_ACCOUNT"
            actionKey="LOGIN"
            targetHref="/(auth)/login"
          />
        </FormCard>
      </FormPageContainer>
      {authLoading && !phoneFlowActive && (
        // Overlay the spinner instead of swapping the FormCard subtree. The
        // ternary used to live here unmounted PhoneAuthFlow mid-await, losing
        // its step/phoneConfirmation state when loading flipped back to false.
        // The phone flow now shows a button-level spinner and keeps its own
        // state alive via the module-level stash, so it doesn't need the
        // overlay at all.
        <View style={StyleSheet.absoluteFillObject} pointerEvents="auto">
          <FormPageContainer>
            <ActivityIndicator size="large" color="#ffffff" />
          </FormPageContainer>
        </View>
      )}
      <AlertModal
        visible={!!modalInfo}
        title={modalInfo?.title ?? ""}
        message={modalInfo?.message ?? ""}
        onClose={() => {
          modalInfo?.onClose?.();
          setModalInfo(null);
        }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewContainer: { flex: 1 },
});

export default SignupScreen;
